import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Wrench, Ban, CheckCircle2, Clock } from 'lucide-react';
import { api } from '../../services/api';

function formatDate(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw || '-';
  const d = raw.slice(0, 10);
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
}

const ROLE_LABELS = {
  transportes: 'Coordinador Área Transportes',
  sst: 'Coordinador SST',
  aprobador: 'Aprobador de preoperacionales',
};

const cleanError = (e, fallback) => String(e?.message || fallback).replace(/^HTTP \d+:\s*/, '');

// Estado de las 3 autorizaciones de la orden (como en el preoperacional).
function SignatureChips({ order }) {
  const chips = [
    { label: 'Transportes', done: !!Number(order.seguridad_signed), by: order.seguridad_signed_by_name },
    { label: 'SST', done: !!Number(order.auth_sst_signed), by: order.auth_sst_signed_by_name },
  ];
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {chips.map((c) => (
        <span key={c.label} title={c.by || undefined}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${c.done ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>
          {c.done ? <CheckCircle2 size={12} /> : <Clock size={12} />} {c.label}{c.done && c.by ? ` · ${c.by}` : ''}
        </span>
      ))}
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
        <Clock size={12} /> Aprobación
      </span>
    </div>
  );
}

function ApproveBox({ order, onDone }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const decide = async (decision) => {
    setError('');
    if (decision === 'bloqueado') {
      if (!notes.trim()) { setError('Escribe el motivo para bloquear la salida.'); return; }
      if (!window.confirm(`¿Bloquear la salida de taller de ${order.plate}? Portería no podrá dejarlo salir.`)) return;
    }
    setBusy(decision);
    try {
      const res = await api.maintenanceServiceOrders.approveExit({ id: order.id, decision, notes: notes.trim() });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible guardar la decisión.');
      onDone(order.id, 'aprobador');
    } catch (e) {
      setError(cleanError(e, 'No fue posible guardar la decisión.'));
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="mt-2 w-full border-t pt-2">
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
        className="w-full rounded-lg border px-2 py-1.5 text-xs"
        placeholder="Observación (opcional para aprobar, obligatoria para bloquear)" />
      {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" disabled={!!busy || order.programada} onClick={() => decide('aprobado')}
          title={order.programada ? 'Se aprueba el día de la salida' : undefined}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60" style={{ backgroundColor: '#16A34A' }}>
          <ShieldCheck size={14} /> {busy === 'aprobado' ? 'Aprobando...' : 'Aprobar salida'}
        </button>
        <button type="button" disabled={!!busy} onClick={() => decide('bloqueado')}
          className="flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60">
          <Ban size={14} /> {busy === 'bloqueado' ? 'Bloqueando...' : 'Bloquear salida'}
        </button>
      </div>
    </div>
  );
}

// Bandeja de salidas de taller (FT-TRP-130). Mismo flujo que el
// preoperacional: firman Coordinador Área Transportes y Coordinador SST (en
// paralelo), luego el aprobador de preoperacionales aprueba o bloquea; solo
// entonces Portería deja registrar la salida. El backend filtra por rol,
// sede y vigencia (7 días) y manda en pending_roles lo que le toca a cada uno.
// embedded: se muestra dentro de una pestaña de Portería; onInfo avisa si
// el usuario puede autorizar (para mostrar la pestaña) y cuántas hay.
export default function SeguridadPendingExits({ location, embedded = false, onInfo }) {
  const [canAuthorize, setCanAuthorize] = useState(false);
  const [items, setItems] = useState([]);
  const [expiredCount, setExpiredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [signingKey, setSigningKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.maintenanceServiceOrders.pendingSeguridad({ sede: location || '' });
      setItems(res?.ok && Array.isArray(res.data) ? res.data : []);
      setExpiredCount(Number(res?.expired_count || 0));
      setCanAuthorize(!!res?.can_authorize);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => { load(); }, [load]);

  const removeRole = (id, role) => {
    setItems((prev) => prev
      .map((it) => (it.id === id ? { ...it, pending_roles: (it.pending_roles || []).filter((r) => r !== role) } : it))
      .filter((it) => (it.pending_roles || []).length > 0));
  };

  const sign = async (order, role) => {
    if (!window.confirm(`¿Autorizar como ${ROLE_LABELS[role]} la salida de taller del vehículo ${order.plate} (${order.order_number})?`)) return;
    setSigningKey(`${order.id}-${role}`);
    try {
      const res = await api.maintenanceServiceOrders.authorizeExit({ id: order.id, role });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible autorizar la salida.');
      removeRole(order.id, role);
    } catch (e) {
      alert(cleanError(e, 'No fue posible autorizar la salida.'));
    } finally {
      setSigningKey('');
    }
  };

  useEffect(() => { if (onInfo) onInfo({ visible: canAuthorize, count: items.length }); }, [onInfo, canAuthorize, items.length]);

  if (embedded && !loading && items.length === 0) {
    return <div className="p-4 text-center text-sm text-gray-500">No hay salidas de taller por autorizar en esta sede.</div>;
  }
  if (loading || items.length === 0) return null;

  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="mb-1 flex items-center gap-2">
        <Wrench size={18} className="text-amber-700" />
        <h3 className="text-sm font-semibold text-amber-900">Salidas de taller por autorizar ({items.length})</h3>
      </div>
      <p className="mb-3 text-xs text-amber-700">
        Órdenes de servicio (FT-TRP-130) de esta sede. Portería deja salir el vehículo cuando firmaron Coordinador Área Transportes y Coordinador SST y el aprobador de preoperacionales la aprobó.
        {expiredCount > 0 && ` Hay ${expiredCount} con más de 7 días sin autorizar: aparecen en Portería › Vencidos.`}
      </p>
      <div className="space-y-2">
        {items.map((order) => (
          <div key={order.id} className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-amber-200 bg-white p-3">
            <div>
              <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900">
                {order.plate} · {order.order_number}
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900">
                  Te toca: {(order.pending_roles || []).map((r) => ROLE_LABELS[r] || r).join(', ')}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {order.workshop_name || 'Sin taller'} · Salida: {formatDate(order.exit_date)} {order.exit_time || ''}
                {order.programada && <span className="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">Programada</span>}
              </div>
              {Number(order.total_cost) > 0 && (
                <div className="text-xs text-gray-500">Costo total: ${Number(order.total_cost).toLocaleString('es-CO')}</div>
              )}
              <SignatureChips order={order} />
            </div>
            <div className="flex flex-wrap gap-2">
              {(order.pending_roles || []).filter((r) => r !== 'aprobador').map((role) => (
                <button
                  key={role}
                  type="button"
                  disabled={signingKey === `${order.id}-${role}` || order.programada}
                  title={order.programada ? 'Se autoriza el día de la salida' : undefined}
                  onClick={() => sign(order, role)}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                  style={{ backgroundColor: '#16A34A' }}
                >
                  <ShieldCheck size={14} /> {signingKey === `${order.id}-${role}` ? 'Autorizando...' : `Autorizar (${role === 'sst' ? 'SST' : 'Transportes'})`}
                </button>
              ))}
            </div>
            {(order.pending_roles || []).includes('aprobador') && <ApproveBox order={order} onDone={removeRole} />}
          </div>
        ))}
      </div>
    </div>
  );
}
